import { showError } from "./ErrorPopup";
import React, { useEffect, useRef, useState } from "react";
import { TrashIcon } from "@heroicons/react/solid";
import { v4 as uuidv4 } from "uuid"; // Génère des identifiants uniques
import { initLightboxJS } from "lightbox.js-react"; // Librairie Lightbox pour l'affichage des images
import Resizer from "react-image-file-resizer";
import { FilePreviewModal } from "./FilePreviewModal";
import ExpenseDatePicker from "./ExpenseDatePicker";
import { apiFetch } from '../utils/apiClient';
const DepenseSchema = ({
  renderError,
  user,
  formData,
  setFormData,
  funds,
  centreCouts,
  isReadOnly,
  page,
}) => {
  // Liste des dépenses
  const [depenses, setDepenses] = useState([]);
  const previousBudgetRef = useRef({
    funds: formData.funds,
    centreCout: formData.centreCout,
    ordre: formData.ordre,
  });
  // URLs de prévisualisation des fichiers uploadés
  const [previewUrls, setPreviewUrls] = useState({});
  // Gestion du popup de prévisualisation
  const [showPopup, setShowPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  // Données provenant de l’API
  const [typeDepenses, setTypeDepenses] = useState([]);
  const [natureDepenses, setNatureDepenses] = useState([]);
  const [TauxKilometrage, setTauxKilometrage] = useState({});
  // URL du fichier sélectionné
  const fileUrl = selectedFile ? URL.createObjectURL(selectedFile) : "";
  // Gestion des erreurs d’upload de fichiers
  const [fileUploadError, setFileUploadError] = useState({});

  // Initialisation de Lightbox
  useEffect(() => {
    initLightboxJS("Insert_Your_License_Key_Or_Use_Default", "default");
  }, []);

  // Synchronisation des dépenses locales avec formData
  useEffect(() => {
    setDepenses(formData.depenses);
  }, [formData.depenses]);

  // Synchroniser les valeurs du compte budgétaire avec les lignes qui
  // utilisaient encore les anciennes valeurs ou qui sont toujours vides.
  useEffect(() => {
    const previousBudget = previousBudgetRef.current;
    const currentBudget = {
      funds: formData.funds,
      centreCout: formData.centreCout,
      ordre: formData.ordre,
    };

    const budgetChanged =
      previousBudget.funds !== currentBudget.funds ||
      previousBudget.centreCout !== currentBudget.centreCout ||
      previousBudget.ordre !== currentBudget.ordre;

    if (!budgetChanged) return;

    setFormData((prevFormData) => {
      let hasChanges = false;
      const updatedDepenses = (prevFormData.depenses || []).map((depense) => ({
        ...depense,
        rows: (depense.rows || []).map((row) => {
          const updatedRow = { ...row };

          if (!row.funds || row.funds === previousBudget.funds) {
            updatedRow.funds = currentBudget.funds || "";
          }
          if (!row.centreCouts || row.centreCouts === previousBudget.centreCout) {
            updatedRow.centreCouts = currentBudget.centreCout || "";
          }
          if (!row.ordre || row.ordre === previousBudget.ordre) {
            updatedRow.ordre = currentBudget.ordre || "";
          }

          if (
            updatedRow.funds !== row.funds ||
            updatedRow.centreCouts !== row.centreCouts ||
            updatedRow.ordre !== row.ordre
          ) {
            hasChanges = true;
          }

          return updatedRow;
        }),
      }));

      return hasChanges
        ? { ...prevFormData, depenses: updatedDepenses }
        : prevFormData;
    });

    previousBudgetRef.current = currentBudget;
  }, [formData.funds, formData.centreCout, formData.ordre, setFormData]);

  

  /**
   * Met à jour un champ d’une dépense
   */
  const handleDepenseChange = (depenseId, field, value) => {
    const newDepenses = [...depenses];
    const depense = newDepenses.find((d) => d.id === depenseId);
    if (depense) {
      depense[field] = value;
      setDepenses(newDepenses);
    }
  };

  /**
   * Met à jour un champ d’une ligne de dépense
   */
  const handleRowChange = (depenseId, rowId, field, value) => {
    const newDepenses = [...depenses];
    const depense = newDepenses.find((d) => d.id === depenseId);
    const row = depense?.rows.find((r) => r.rowId === rowId);
    if (row) {
      row[field] = value;
      // Réinitialisation si ce n’est pas du kilométrage
      if (field === "typeDepenses" && value !== "Kilométrage") {
        row.kms = null; // Reset kms to null if typeDepenses is not "Kilométrage"
        row.montantRembourser = null;
        row.montant = null;
      }
      // Paramétrage automatique pour le kilométrage
      if (field === "typeDepenses" && value === "Kilométrage") {
        row.fournisseur = ""; // Reset kms to null if typeDepenses is not "Kilométrage"
        row.montant = null;
        row.montantRembourser = null;
        row.natureDepenses = "Kilométrage - déplacement";
        row.compte = 36200;
        row.codeTaxe = "AA";
      }

      if (field === "montant" && value === "Kilométrage") {
        row.fournisseur = ""; // Reset kms to null if typeDepenses is not "Kilométrage"
        row.montant = null;
        row.montantRembourser = null;
        row.natureDepenses = "Kilométrage - déplacement";
        row.compte = 36200;
        row.codeTaxe = "AA";
      }
      // Calcul automatique du montant basé sur les kilomètres
      if (field === "kms") {
        // Créer une date sans l'heure pour la comparaison (utilisation de toISOString pour récupérer uniquement la date)
        const reclamationDate = new Date(depense.dateDepense)
          .toISOString()
          .split("T")[0]; // "YYYY-MM-DD"

        // Chercher un taux de kilométrage où la date de la réclamation est entre Datedebut et DatedeFin (sans heure)
        const tauxKilometrage = TauxKilometrage.find((t) => {
          const startDate = new Date(t.Datedebut).toISOString().split("T")[0]; // "YYYY-MM-DD"
          const endDate = t.DatedeFin
            ? new Date(t.DatedeFin).toISOString().split("T")[0]
            : null; // "YYYY-MM-DD" ou null si non défini

          // Vérifier si la date de réclamation est après la startDate, et avant endDate si elle existe
          return (
            reclamationDate >= startDate &&
            (endDate ? reclamationDate <= endDate : true)
          );
        });

        if (tauxKilometrage) {
          row.montant = parseFloat(row.kms * tauxKilometrage.value).toFixed(2); // Calculer le montant si la période de réclamation est dans l'intervalle
        }
      }
      setDepenses(newDepenses);
      setFormData({ ...formData, depenses: newDepenses });
    }
  };

  /**
   * Ajoute une nouvelle ligne à une dépense
   */
  const addRow = (depenseId) => {
    const newDepenses = [...depenses];
    const depense = newDepenses.find((d) => d.id === depenseId);
    if (depense) {
      depense.rows.push({
        rowId: uuidv4(),
        typeDepenses: "",
        kms: null,
        fournisseur: "",
        natureDepenses: "",
        compte: null,
        codeTaxe: "",
        funds: formData.funds,
        centreCouts: formData.centreCout,
        ordre: formData.ordre,
        montant: null,
        montantRembourser: null,
        commentaire: "",
      });

      setDepenses(newDepenses);
      setFormData({ ...formData, depenses: newDepenses });
    }
  };

  /**
   * Supprime une ligne d’une dépense
   */
  const removeRow = (depenseId, rowId) => {
    const newDepenses = [...depenses];
    const depense = newDepenses.find((d) => d.id === depenseId);
    if (depense) {
      depense.rows = depense.rows.filter((row) => row.rowId !== rowId);
      setDepenses(newDepenses);
      setFormData({ ...formData, depenses: newDepenses });
    }
  };

  /**
   * Ajoute une nouvelle dépense complète
   */
  // const addDepense = async() => {
  //   const newDepenses = [
  //     ...depenses,
  //     {
  //       id: uuidv4(),
  //       dateDepense: "",
  //       descriptionDepense: "",
  //       deplacement: "",
  //       trajetFiles: [],
  //       rows: [
  //         {
  //           rowId: uuidv4(),
  //           typeDepenses: "",
  //           kms: null,
  //           fournisseur: "",
  //           natureDepenses: "",
  //           compte: null,
  //           codeTaxe: "",
  //           funds: formData.funds,
  //           centreCouts: formData.centreCout,
  //           ordre: formData.ordre,
  //           montant: null,
  //           montantRembourser: null,
  //           commentaire: "",
  //         },
  //       ],
  //       tousReçusFournis: "oui",
  //       files: [],
  //     },
  //   ];
  //   setDepenses(newDepenses);
  //   setFormData({ ...formData, depenses: newDepenses });

  // };

  const addDepense = async () => {
  const nouvelleDepense = {
    id: uuidv4(),
    dateDepense: "",
    descriptionDepense: "",
    deplacement: "",
    trajetFiles: [],
    rows: [
      {
        rowId: uuidv4(),
        typeDepenses: "",
        kms: null,
        fournisseur: "",
        natureDepenses: "",
        compte: null,
        codeTaxe: "",
        funds: formData.funds,
        centreCouts: formData.centreCout,
        ordre: formData.ordre,
        montant: null,
        montantRembourser: null,
        commentaire: "",
      },
    ],
    tousReçusFournis: "oui",
    files: [],
  };
 try{
  const response = await apiFetch(
    `${process.env.REACT_APP_API_BASE_URL}/formReclamationDepense/${formData.numeroFormulaire}/depense`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        depense: nouvelleDepense,
      }),
    }
  );
const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message);
      }
  
    const newDepenses = [...depenses, nouvelleDepense];

    setDepenses(newDepenses);
    setFormData({
      ...formData,
      depenses: newDepenses,
    });
  
}  catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    }
};

  /**
   * Supprime une dépense complète
   */
  const removeDepense = async (depenseId) => {
  try {
    const response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/deleteDepense`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          numeroFormulaire: formData.numeroFormulaire,
          depenseId,
        }),
      }
    );

    const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message);
      }
    // Mise à jour du frontend
    const newDepenses = depenses.filter(
      (d) => d.id !== depenseId
    );

    setDepenses(newDepenses);
    setFormData({
      ...formData,
      depenses: newDepenses,
    });

  } catch (error) {
    console.error(error);
    showError(error); // ou setError(error.message)
  }
};

  const handleFileUpload = async (e, depenseId, type = "facture") => {
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
    const files = Array.from(e.target.files) || [];

    const errorKey = `${depenseId}_${type}`;

    const ALLOWED_TYPES = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    const invalidFiles = files.filter(
      (file) => !ALLOWED_TYPES.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      setFileUploadError((prev) => ({
        ...prev,
        [errorKey]: "Formats acceptés : PDF, JPG, JPEG, PNG.",
      }));
      return;
    }

    // Resize images
    const resizeImage = (file) =>
      new Promise((resolve) => {
        if (!file.type.includes("image")) return resolve(file); // Skip non-images

        Resizer.imageFileResizer(
          file,
          1600, // maxWidth
          1600, // maxHeight
          "WEBP", // format de sortie
          70, // qualité (70% pour captures écrans)
          0, // rotation
          (uri) => {
            const resizedFile = new File([uri], file.name, {
              type: "image/webp",
            });
            resolve(resizedFile);
          },
          "blob",
          200, // minWidth
          200, // minHeight
        );
      });

    const compressPDF = async (file) => {
      if (file.type !== "application/pdf") return file;

      // Conserver les octets originaux afin de ne pas altérer les PDF
      // protégés par mot de passe, signés ou contenant des formulaires.
      // Le fichier sera ensuite chiffré par le backend avant son stockage.
      return file;
    };

    // Traitement par type
    const processFile = async (file) => {
      if (file.type.includes("image")) {
        return await resizeImage(file);
      } else if (file.type === "application/pdf") {
        return await compressPDF(file);
      }
      return file;
    };

    const processedFiles = await Promise.all(files.map(processFile));

    // Vérifier après traitement si certains fichiers sont trop gros
    const oversizedFiles = processedFiles.filter(
      (file) => file.size > MAX_FILE_SIZE,
    );
    if (oversizedFiles.length > 0) {
      setFileUploadError((prev) => ({
        ...prev,
        [errorKey]: `Un ou plusieurs fichiers (même après compression) dépassent ${MAX_FILE_SIZE_MB} Mo.`,
      }));
      return;
    }

    setFileUploadError((prev) => ({
      ...prev,
      [errorKey]: "",
    }));

    const newDepenses = [...depenses];
    const depense = newDepenses.find((d) => d.id === depenseId);

    if (depense) {
      // const existingFiles = depense.files || [];
      const existingFiles =
        type === "trajet" ? depense.trajetFiles || [] : depense.files || [];
      // Récupère les noms existants
      const existingFileNames = new Set(
        existingFiles.map((f) => f.name || f.filename),
      );

      // Garde uniquement les fichiers dont le nom n'existe pas déjà
      const filteredFiles = processedFiles.filter(
        (file) => !existingFileNames.has(file.name),
      );

      if (filteredFiles.length < processedFiles.length) {
        setFileUploadError((prev) => ({
          ...prev,
          [errorKey]: "Un fichier avec ce nom existe déjà.",
        }));
      } else {
        setFileUploadError((prev) => ({
          ...prev,
          [errorKey]: "",
        }));
      }

      // Upload des nouveaux fichiers vers le backend
      if (filteredFiles.length > 0) {
        try{
        const uploadFormData = new FormData();

        uploadFormData.append("numeroFormulaire", formData.numeroFormulaire);
        uploadFormData.append("depenseId", depenseId);
        uploadFormData.append("type", type);

        filteredFiles.forEach((file) => {
          uploadFormData.append("files", file);
        });
        
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/uploadFiles`,
          {
            method: "POST",
            body: uploadFormData,
          }
        );

        const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message);
      }

        // Ajouter le _id retourné par le backend dans chaque objet File
        filteredFiles.forEach((file) => {
          const uploaded = data.uploadedFiles.find(
            (f) => f.filename === file.name
          );

          if (uploaded) {
            file._id = uploaded._id;
            depense.fileInfos = [
              ...(depense.fileInfos || []),
              uploaded.fileInfo,
            ];
          }
        });
      } catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    }
    }

      const updatedFiles = [...existingFiles, ...filteredFiles];
      // depense.files = updatedFiles;
      if (type === "trajet") {
        depense.trajetFiles = updatedFiles;
      } else {
        depense.files = updatedFiles;
      }
      const newPreviewUrls = { ...previewUrls };
      filteredFiles.forEach((file) => {
        newPreviewUrls[file.name] = URL.createObjectURL(file);
      });

      setDepenses(newDepenses);
      setPreviewUrls(newPreviewUrls);
      setFormData({ ...formData, depenses: newDepenses });
    }
  };

  const handleFileDelete = async (fileId,fileName, depenseId, type = "facture") => {
    try {
    // Supprimer du backend
    const response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/deleteFile`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          numeroFormulaire: formData.numeroFormulaire,
          depenseId,
          fileId,
          type,
        }),
      }
    );
const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message);
      }
   

    const newDepenses = [...depenses]; // Copiez les dépenses existantes

    // Trouver la dépense par son id
    const depense = newDepenses.find((d) => d.id === depenseId);

    if (depense) {
      // const updatedFiles = depense.files.filter(
      //   (file) => file.name !== fileName
      // );
      const updatedFiles = (
        type === "trajet" ? depense.trajetFiles : depense.files
      ).filter((file) => file.name !== fileName);
      // Supprimer le fichier de la dépense spécifique
      if (type === "trajet") {
        depense.trajetFiles = updatedFiles;
      } else {
        depense.files = updatedFiles;
      }
      // depense.files = updatedFiles;

      const updatedPreviewUrls = { ...previewUrls };
      delete updatedPreviewUrls[fileName]; // Supprimer l'URL de prévisualisation du fichier supprimé

      setDepenses(newDepenses); // Mettez à jour l'état des dépenses
      setPreviewUrls(updatedPreviewUrls); // Mettez à jour les URLs de prévisualisation
      setFormData({ ...formData, depenses: newDepenses }); // Mettez à jour formData
    }
    } catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    }
  };

  // Récupération des types et natures de dépenses et des taux de kilométrage depuis l’API
  useEffect(() => {
  Promise.all([
    apiFetch(`${process.env.REACT_APP_API_BASE_URL}/TypeDepense`),
    apiFetch(`${process.env.REACT_APP_API_BASE_URL}/NatureDepense`),
    apiFetch(`${process.env.REACT_APP_API_BASE_URL}/TauxKilometrage`),
  ])
    .then(async ([typeRes, natureRes, tauxRes]) => {
      const [typeData, natureData, tauxData] = await Promise.all([
        typeRes.json(),
        natureRes.json(),
        tauxRes.json(),
      ]);

      setTypeDepenses(typeData);
      setNatureDepenses(natureData);
      setTauxKilometrage(tauxData);
    })
    .catch((error) => {
      console.error("Erreur lors du chargement des données :", error);
    });
}, []);

 
  const isCurrentTraitant =
    formData.etatDemande === "En traitement" &&
    formData.traitant?.at(-1)?.userId === user.id &&
    page==="Mes taches";

  const isOwnerEditing =
    (formData.etatDemande === "Brouillon" ||
    formData.etatDemande === "Rejeté") &&
    formData.userId === user.id &&
    page!=="Mes taches";
  
  const isAdminEditing =(!isReadOnly && formData.etatDemande === "En traitement" && page==="Mes taches")

  const canUploadFile = isOwnerEditing || isCurrentTraitant || isAdminEditing;

  return (
    <div className="mt-6">
      {depenses.map((depense, index) => (
        <div
          key={depense.id}
          className="mb-6 border p-4 rounded-lg shadow-md bg-gray-50 "
        >
          
  
  <h3 className="font-semibold text-xl text-gray-900 tracking-tight">
    Dépense {index + 1}
  </h3>


          {/* Date and Description for Each Depense */}
          <div className="mt-5 w-3/4 grid grid-cols-2 gap-4">
            <div className="w-3/5">
              <label className="block text-sm font-medium text-blue-900">
                Date <span className="text-xl text-red-900">*</span> :
              </label>
              <ExpenseDatePicker
                value={depense.dateDepense}
                onChange={(date) =>
                  handleDepenseChange(depense.id, "dateDepense", date)
                }
                period={formData.periodReclamation}
                excludedDates={depenses
                  .filter(
                    (otherDepense) =>
                      otherDepense.id !== depense.id &&
                      otherDepense.dateDepense,
                  )
                  .map((otherDepense) => otherDepense.dateDepense)}
                disabled={isReadOnly}
              />              {renderError(`dateDepense_${depense.id}`)}
            </div>

            <div className="w-3/5">
              <label className="block text-sm font-medium text-blue-900">
                Description du déplacement ou de la dépense{" "}
                <span className="text-xl text-red-900">*</span> :
              </label>
              <textarea
                placeholder="Ajoutez une description..."
                className="mt-1 w-full p-3 border border-gray-200 rounded-xl  shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200"
                rows="2"
                maxLength={50} // Limitation à 50 caractères
                value={depense.descriptionDepense}
                onChange={(e) =>
                  handleDepenseChange(
                    depense.id,
                    "descriptionDepense",
                    e.target.value,
                  )
                }
                readOnly={isReadOnly}
              ></textarea>
              {renderError(`descriptionDepense_${depense.id}`)}
            </div>
            {depense.rows.some((row) => row.typeDepenses === "Kilométrage") && (
              <div className="w-3/5">
                <label className="block text-sm font-medium text-blue-900">
                  Point de départ-Destination-Point de retour{" "}
                  <span className="text-xl text-red-900">*</span> :
                </label>
                <textarea
                  placeholder=""
                  className="mt-1 w-full p-3 border border-gray-200 rounded-xl  shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200"
                  rows="2"
                  value={depense.deplacement}
                  onChange={(e) =>
                    handleDepenseChange(
                      depense.id,
                      "deplacement",
                      e.target.value,
                    )
                  }
                  disabled={isReadOnly}
                ></textarea>
                {renderError(`deplacement_${depense.id}`)}
              </div>
            )}
            {depense.rows.some((row) => row.typeDepenses === "Kilométrage") && (
              <div className="w-3/5">
                <label
                  htmlFor="fileUpload"
                  className="block text-sm font-medium text-blue-900 mb-2"
                >
                  Veuillez joindre une capture du trajet{" "}:
                  {/* <span className="text-xl text-red-900">*</span> : */}
                </label>
              
                {canUploadFile &&(
                <div>
                  <div className="relative">
                    <input
                      type="file"
                      id="fileUpload"
                      name="fileUpload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple // Permet l'upload multiple
                      onChange={(e) =>
                        handleFileUpload(e, depense.id, "trajet")
                      }
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      disabled={!canUploadFile}
                      // disabled={isReadOnly}
                    />
                    <button className="w-full px-4 py-2 border border-blue-500 rounded-lg text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
                      Choisir des fichiers
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Formats acceptés : PDF, JPG, JPEG, PNG.
                  </p>
                  {renderError(`trajetFiles_${depense.id}`)}
                  {fileUploadError[`${depense.id}_trajet`] && (
                    <p style={{ color: "red", marginTop: "0.5rem" }}>
                      {fileUploadError[`${depense.id}_trajet`]}
                    </p>
                  )}
                </div>)}
               
                
                {depense.trajetFiles && depense.trajetFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {depense.trajetFiles
                      ?.filter((f) => f !== null && f !== undefined)
                      .map((file) => {
                        const canDeleteFile =
                          isOwnerEditing ||
                          isAdminEditing||
                          (
                            isCurrentTraitant &&
                            depense.fileInfos?.some(
                              info => info.fileId === file._id && info.userId === user.id
                            )
                          );
                          
                          
                        const filename = file.name || file.filename || "";
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                          filename,
                        );
                        const isPdf = /\.pdf$/i.test(filename);

                        return (
                          <div
                            key={file._id || filename}
                            className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-white shadow-sm hover:bg-blue-50 transition-colors duration-150"
                          >
                            {/* Icône selon le type */}
                            <span className="text-lg flex-shrink-0">
                              {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
                            </span>

                            {/* Nom + sous-titre cliquable */}
                            <button
                              onClick={() => {
                                setSelectedFile(file);
                                setShowPopup(true);
                              }}
                              className="flex-1 min-w-0 text-left group"
                            >
                              <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
                                {filename}
                              </p>
                              <p className="text-xs text-gray-400">
                                Cliquer pour visualiser
                              </p>
                            </button>

                            {/* Flèche d'ouverture */}
                            <span className="text-blue-400 text-xs flex-shrink-0">
                              ↗
                            </span>

                            {/* Bouton supprimer */}
                            { canDeleteFile &&(
                              <button
                                onClick={() =>
                                  handleFileDelete(
                                    file._id,
                                    filename,
                                    depense.id,
                                    "trajet",
                                  )
                                }
                                className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                
              </div>
            )}
          </div>

          {/* Table for Rows */}
          <div className="mt-10">
            {isReadOnly === false && (
              <button
                type="button"
                className="mt-5 bg-blue-500 text-white px-5 py-2 rounded-full text-sm font-medium
                 hover:bg-blue-600 
                 active:scale-95 
                 transition-all duration-150"
                onClick={() => addRow(depense.id)}
              >
                + Ajouter une dépense
              </button>
            )}
            <table className="w-full border border-gray-900 mt-2">
              
              
              <thead className="bg-blue-100">
                <tr>
                  <th className="border border-gray-300 px-10 py-2">
                    Type de dépense
                  </th>
                  <th className="border border-gray-300 px-2 py-1 w-[7%]">
                    Kms
                  </th>
                  <th className="border border-gray-300 px-2 py-2">
                    Fournisseur
                  </th>
                  <th className="border border-gray-300 px-20 py-2">
                    Nature de la dépense
                  </th>
                  <th className="border border-gray-300 px-2 py-2">Compte</th>
                  <th className="border border-gray-300 px-5 py-2">Fonds</th>
                  <th className="border border-gray-300 px-8 py-2">
                    Centre de coût
                  </th>
                  <th className="border border-gray-300 px-3 py-2">
                    Ordre (optionnel)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 w-[6%]">
                    Montant ($)
                  </th>
                  <th className="border border-gray-300 px-3 py-2 w-[6%]">
                    Montant à rembourser au Conseil ($)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 w-[12%]">
                    Commentaires
                  </th>
                  <th className="border border-gray-300 px-1 py-1"></th>
                </tr>
              </thead> 
              <tbody className="w-full border border-gray-900 mt-2">
                {depense.rows.map((row) => (
                  <tr key={row.rowId}>
                    <td className="border p-2">
                      <select
                        id="typeDepenses"
                        name="typeDepenses"
                        value={row.typeDepenses}
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "typeDepenses",
                            e.target.value,
                          )
                        }
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={isReadOnly}
                      >
                        <option value="">Sélectionner</option>
                        {typeDepenses.map((typeDepense) => (
                          <option
                            key={typeDepense._id}
                            value={typeDepense.value}
                          >
                            {typeDepense.value}
                          </option>
                        ))}
                      </select>
                      {renderError(`typeDepenses_${row.rowId}`)}
                    </td>

                    <td className="border p-2 w-[6%]">
                      <input
                        type="text"
                        value={row.kms || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Accepte chiffres + virgule + point
                          if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
                            handleRowChange(
                              depense.id,
                              row.rowId,
                              "kms",
                              value,
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          // Autoriser uniquement les touches numériques, backspace, delete, flèches
                          if (
                            !(
                              (e.key >= "0" && e.key <= "9") ||
                              e.key === "," ||
                              e.key === "." ||
                              e.key === "Backspace" ||
                              e.key === "Delete" ||
                              e.key === "ArrowLeft" ||
                              e.key === "ArrowRight" ||
                              e.key === "Tab"
                            )
                          ) {
                            e.preventDefault();
                          }
                        }}
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        required
                        disabled={
                          row.typeDepenses !== "Kilométrage" ||
                          depense.dateDepense.trim() === ""
                        }
                        readOnly={
                          isReadOnly &&
                          page === "Mes taches" &&
                          (formData.etatDemande === "Rejeté" ||
                            formData.etatDemande === "Approuvé")
                        }
                      />
                      {depense.dateDepense.trim() === "" &&
                        row.typeDepenses === "Kilométrage" && (
                          <p className="text-red-600 text-sm mt-1">
                            Veuillez remplir la date de dépense avant d’ajouter
                            les kilomètres.
                          </p>
                        )}
                      {renderError(`kms_${row.rowId}`)}
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        value={row.fournisseur}
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "fournisseur",
                            e.target.value,
                          )
                        }
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={row.typeDepenses === "Kilométrage"}
                        readOnly={isReadOnly}
                      />
                      {renderError(`fournisseur_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <select
                        id="natureDepenses"
                        name="natureDepenses"
                        value={row.natureDepenses}
                        disabled={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                        onChange={(e) => {
                          const selectedNature = e.target.value;
                          const selectedCompte =
                            natureDepenses.find(
                              (nature) => nature.value === selectedNature,
                            )?.compte || "";
                          const selectedcodeTaxe =
                            natureDepenses.find(
                              (nature) => nature.value === selectedNature,
                            )?.codeTaxe || "";

                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "natureDepenses",
                            selectedNature,
                          );
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "compte",
                            selectedCompte,
                          );
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "codeTaxe",
                            selectedcodeTaxe,
                          );
                        }}
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Sélectionner</option>

                        {(row.typeDepenses === "Kilométrage"
                          ? natureDepenses.filter((nature) =>
                              [
                                "Kilométrage - déplacement",
                                "Kilométrage - formation enseignant",
                                "Kilométrage - formation non-enseignant",
                              ].includes(nature.value),
                            )
                          : natureDepenses.filter((nature) =>
                              row.typeDepenses === "Visa du Conseil"
                                ? nature.value !==
                                  "Hébergement - famille/ami (25$)"
                                : true,
                            )
                        ).map((natureDepense) => (
                          <option
                            key={natureDepense.value}
                            value={natureDepense.value}
                          >
                            {natureDepense.value}
                          </option>
                        ))}
                      </select>

                      {renderError(`natureDepenses_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <input
                        type="text"
                        value={row.compte}
                        // disabled={row.typeDepenses === "Kilométrage"}
                        readOnly={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "compte",
                            e.target.value,
                          )
                        }
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      {renderError(`compte_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <select
                        id="funds"
                        name="funds"
                        value={row.funds}
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "funds",
                            e.target.value,
                          )
                        }
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                      >
                        <option value="">Sélectionner</option>
                        {funds.map((fund) => (
                          <option key={fund._id} value={fund.value}>
                            {fund.value}
                          </option>
                        ))}
                      </select>
                      {renderError(`funds_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <select
                        id="centreCouts"
                        name="centreCouts"
                        value={row.centreCouts}
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "centreCouts",
                            e.target.value,
                          )
                        }
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                      >
                        <option value="">Sélectionner</option>
                        {centreCouts.map((centreCout) => (
                          <option key={centreCout._id} value={centreCout.value}>
                            {centreCout.value}
                          </option>
                        ))}
                      </select>
                      {renderError(`centreCouts_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <input
                        type="text"
                        value={row.ordre}
                        maxLength={6}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Accepte seulement les chiffres (0-9)
                          if (/^\d*$/.test(value)) {
                            handleRowChange(
                              depense.id,
                              row.rowId,
                              "ordre",
                              value,
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          // Autoriser uniquement les touches numériques, backspace, delete, flèches
                          if (
                            !(
                              (e.key >= "0" && e.key <= "9") ||
                              e.key === "Backspace" ||
                              e.key === "Delete" ||
                              e.key === "ArrowLeft" ||
                              e.key === "ArrowRight" ||
                              e.key === "Tab"
                            )
                          ) {
                            e.preventDefault();
                          }
                        }}
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                      />
                    </td>

                    <td className="border p-2 w-[6%]">
                      <div className="relative">
                        <input
                          type="text"
                          step="0.01"
                          value={row.montant ?? ""}
                          disabled={
                            row.typeDepenses === "Kilométrage" ||
                            (isReadOnly &&
                              !(
                                page === "Mes taches" &&
                                formData.etatDemande === "En traitement"
                              ))
                          }
                          onBlur={(e) => {
                            const rawValue = e.target.value.replace(",", "."); // conversion
                            const value = parseFloat(rawValue || 0).toFixed(2);

                            handleRowChange(
                              depense.id,
                              row.rowId,
                              "montant",
                              value,
                            );

                            if (
                              row.typeDepenses === "Visa du Conseil" &&
                              row.natureDepenses ===
                                "Dépense personnelle - Visa"
                            ) {
                              handleRowChange(
                                depense.id,
                                row.rowId,
                                "montantRembourser",
                                value,
                              );
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Accepte chiffres + virgule + point
                            if (/^-?[0-9]*[.,]?[0-9]*$/.test(value)) {
                              handleRowChange(
                                depense.id,
                                row.rowId,
                                "montant",
                                value.replace(",", "."), // conversion aussi ici
                              );
                            }
                          }}
                          //   onBlur={(e) => {
                          //     const value = parseFloat(e.target.value || 0).toFixed(2);
                          //     handleRowChange(depense.id, row.rowId, "montant", value);

                          //     if (
                          //       row.typeDepenses === "Visa du Conseil" &&
                          //       row.natureDepenses === "Dépense personnelle - Visa"
                          //     ) {
                          //       handleRowChange(depense.id, row.rowId, "montantRembourser", value);
                          //     }
                          //   }}

                          //   onChange={(e) => {
                          //   const value = e.target.value;
                          //   // Accepte chiffres + virgule + point
                          //   if (/^[0-9]*[.,-]?[0-9]*$/.test(value)) {
                          //     handleRowChange(depense.id, row.rowId, "montant", value);
                          //   }
                          // }}
                          onKeyDown={(e) => {
                            // Autoriser uniquement les touches numériques, backspace, delete, flèches
                            if (
                              !(
                                (e.key >= "0" && e.key <= "9") ||
                                e.key === "," ||
                                e.key === "." ||
                                e.key === "-" ||
                                e.key === "Backspace" ||
                                e.key === "Delete" ||
                                e.key === "ArrowLeft" ||
                                e.key === "ArrowRight" ||
                                e.key === "Tab"
                              )
                            ) {
                              e.preventDefault();
                            }
                          }}
                          className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                          $
                        </span>
                      </div>
                      {renderError(`montant_${row.rowId}`)}
                    </td>

                    <td className="border p-2 w-[6%]">
                      <div className="relative">
                        <input
                          type="text"
                          step="0.01"
                          value={row.montantRembourser ?? ""}
                          disabled={
                            row.typeDepenses === "Kilométrage" ||
                            row.typeDepenses === "Autre dépense" ||
                            (row.typeDepenses === "Visa du Conseil" &&
                              row.natureDepenses ===
                                "Dépense personnelle - Visa") ||
                            (isReadOnly &&
                              !(
                                page === "Mes taches" &&
                                formData.etatDemande === "En traitement"
                              ))
                          }
                          onBlur={(e) => {
                            let value = e.target.value.replace(",", "."); // remplace la virgule par un point
                            handleRowChange(
                              depense.id,
                              row.rowId,
                              "montantRembourser",
                              parseFloat(value || 0).toFixed(2),
                            );
                          }}
                          onChange={(e) => {
                            const value = e.target.value.replace(",", "."); // idem ici
                            // Accepte chiffres + virgule + point
                            if (/^-?[0-9]*[.,]?[0-9]*$/.test(value)) {
                              handleRowChange(
                                depense.id,
                                row.rowId,
                                "montantRembourser",
                                value,
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            // Autoriser uniquement les touches numériques, backspace, delete, flèches
                            if (
                              !(
                                (e.key >= "0" && e.key <= "9") ||
                                e.key === "," ||
                                e.key === "." ||
                                e.key === "-" ||
                                e.key === "Backspace" ||
                                e.key === "Delete" ||
                                e.key === "ArrowLeft" ||
                                e.key === "ArrowRight" ||
                                e.key === "Tab"
                              )
                            ) {
                              e.preventDefault();
                            }
                          }}
                          //   onBlur={(e) =>
                          //     handleRowChange(
                          //       depense.id,
                          //       row.rowId,
                          //       "montantRembourser",
                          //       parseFloat(e.target.value || 0).toFixed(2)
                          //     )
                          //   }
                          //   onChange={(e) => {
                          //   const value = e.target.value;
                          //   // Accepte chiffres + virgule + point
                          //   if (/^[0-9]*[.,-]?[0-9]*$/.test(value)) {
                          //     handleRowChange(depense.id, row.rowId, "montantRembourser", value);
                          //   }
                          // }}

                          className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                          $
                        </span>
                      </div>
                      {renderError(`montantRembourser_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      <textarea
                        placeholder="Ajoutez un commentaire..."
                        className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        rows="2"
                        value={row.commentaire}
                        onChange={(e) =>
                          handleRowChange(
                            depense.id,
                            row.rowId,
                            "commentaire",
                            e.target.value,
                          )
                        }
                        disabled={
                          isReadOnly &&
                          !(
                            page === "Mes taches" &&
                            formData.etatDemande === "En traitement"
                          )
                        }
                      ></textarea>
                      {renderError(`commentaire_${row.rowId}`)}
                    </td>

                    <td className="border p-2">
                      {depense.rows.length > 1 && isReadOnly === false && (
                        <button
                          // className=" px-2 py-1 rounded hover:bg-red-600"
                          className="w-8 h-10 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200"
                          onClick={() => removeRow(depense.id, row.rowId)}
                        >
                          <TrashIcon className="w-7 h-7" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-10 w-3/4 grid grid-cols-2 gap-0">
              <div className="w-3/5">
                <label
                  htmlFor={`tousReçusFournis-${depense.id}`}
                  className="block text-sm font-semibold text-blue-900 mb-1"
                >
                  Avez-vous tous les reçus pour les dépenses ci-dessus :{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="mt-2 flex space-x-3">
                  {["oui", "non"].map((option) => {
                    const isSelected = depense.tousReçusFournis === option;
                    return (
                      <label
                        key={option}
                        // className={`cursor-pointer px-3 py-1 text-sm rounded-md border transition-colors duration-150
                        // ${isSelected
                        //     ? "bg-blue-600 text-white border-blue-600"
                        //     : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                        //   }`}
                        className={`
                  flex items-center justify-center w-[80px]
                  px-4 py-2 rounded-lg cursor-pointer
                  text-sm font-medium
                  border transition-all duration-200
                  focus-within:ring-2 focus-within:ring-blue-300 focus-within:ring-offset-1
                  ${
                    isSelected
                      ? "bg-blue-100 text-blue-600 border-blue-300"
                      : "bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-600"
                  }
                `}
                      >
                        <input
                          type="radio"
                          name={`tousReçusFournis-${depense.id}`}
                          value={option}
                          onChange={(e) =>
                            handleDepenseChange(
                              depense.id,
                              "tousReçusFournis",
                              e.target.value,
                            )
                          }
                          className="hidden"
                          disabled={isReadOnly}
                        />
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="w-3/5">
                <label
                  htmlFor="fileUpload"
                  className="block text-sm font-medium text-blue-900 mb-2"
                >
                  Veuillez joindre une copie de vos factures{" "}
                  {depense.rows.some(
                    (row) =>
                      row.typeDepenses !== "Kilométrage" &&
                      !(row.natureDepenses === "Dépense personnelle - Visa") &&
                      !(
                        row.natureDepenses === "Hébergement - famille/ami (25$)"
                      ) &&
                      !(depense.tousReçusFournis === "non") &&
                      !(row.natureDepenses === "407 ETR"),
                  ) && <span className="text-xl text-red-900">*</span>}{" "}
                  :
                </label>
                
                {canUploadFile &&(
                <div>
                  <div className="relative">
                    <input
                      type="file"
                      id="fileUpload"
                      name="fileUpload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple // Permet l'upload multiple
                      onChange={(e) => handleFileUpload(e, depense.id)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      disabled={!canUploadFile}
                      // disabled={isReadOnly}
                    />
                    <button className="w-full px-4 py-2 border border-blue-500 rounded-lg text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
                      Choisir des fichiers
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Formats acceptés : PDF, JPG, JPEG, PNG.
                  </p>
                  {renderError(`files_${depense.id}`)}
                  {fileUploadError[`${depense.id}_facture`] && (
                    <p style={{ color: "red", marginTop: "0.5rem" }}>
                      {fileUploadError[`${depense.id}_facture`]}
                    </p>
                  )}
                </div>)}

                {depense.files && depense.files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {depense.files
                      ?.filter((f) => f !== null && f !== undefined)
                      .map((file) => {
                         const canDeleteFile =
                          isOwnerEditing ||
                          isAdminEditing ||
                          (
                            isCurrentTraitant &&
                            depense.fileInfos?.some(
                              info => info.fileId === file._id && info.userId === user.id
                            )
                          ) ;

                        const filename = file.name || file.filename || "";
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                          filename,
                        );
                        const isPdf = /\.pdf$/i.test(filename);

                        return (
                          <div
                            key={file._id || filename}
                            className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-white shadow-sm hover:bg-blue-50 transition-colors duration-150"
                          >
                            {/* Icône selon le type */}
                            <span className="text-lg flex-shrink-0">
                              {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
                            </span>

                            {/* Nom + sous-titre cliquable */}
                            <button
                              onClick={() => {
                                setSelectedFile(file);
                                setShowPopup(true);
                              }}
                              className="flex-1 min-w-0 text-left group"
                            >
                              <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
                                {filename}
                              </p>
                              <p className="text-xs text-gray-400">
                                Cliquer pour visualiser
                              </p>
                            </button>

                            {/* Flèche d'ouverture */}
                            <span className="text-blue-400 text-xs flex-shrink-0">
                              ↗
                            </span>

                            {/* Bouton supprimer */}
                            {canDeleteFile&& (
                              <button
                                onClick={() =>
                                  handleFileDelete(
                                    file._id,
                                    filename,
                                    depense.id,
                                    "facture",
                                  )
                                }
                                className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Remove Depense Button */}

          {depenses.length > 1 && isReadOnly === false && (
            <button
              type="button"
              className="mt-5 bg-red-500 text-white px-5 py-2 rounded-full text-sm font-medium
                 hover:bg-red-600 
                 active:scale-95 
                 transition-all duration-150"              
              onClick={() => removeDepense(depense.id)}
            >
              Supprimer la date
            </button>
          )}
        </div>
      ))}

      {/* Add Depense Button */}
      {isReadOnly === false && (
        <button
          type="button"
          className="mt-5 bg-blue-500 text-white px-5 py-2 rounded-full text-md font-medium
                 hover:bg-blue-600 
                 active:scale-95 
                 transition-all duration-150"
          onClick={addDepense}
        >
          + Ajouter une date
        </button>
      )}
      {renderError("depenses")}

      {/* Popup pour la prévisualisation */}
                {showPopup && selectedFile && (
                  <FilePreviewModal
                    selectedFile={selectedFile}
                    fileUrl={fileUrl}
                    onClose={() => setShowPopup(false)}
                  />
                )}
    </div>
  );
};

export default DepenseSchema;











// import React, { useEffect, useState } from "react";
// import { TrashIcon } from "@heroicons/react/solid";
// import { v4 as uuidv4 } from "uuid"; // Génère des identifiants uniques
// import { initLightboxJS } from "lightbox.js-react"; // Librairie Lightbox pour l'affichage des images
// import Resizer from "react-image-file-resizer";
// import { PDFDocument } from "pdf-lib";
// import { FilePreviewModal } from "./FilePreviewModal";
// const DepenseSchema = ({
//   renderError,
//   formData,
//   setFormData,
//   funds,
//   centreCouts,
//   isReadOnly,
//   page,
// }) => {
//   // Liste des dépenses
//   const [depenses, setDepenses] = useState([]);
//   // URLs de prévisualisation des fichiers uploadés
//   const [previewUrls, setPreviewUrls] = useState({});
//   // Gestion du popup de prévisualisation
//   const [showPopup, setShowPopup] = useState(false);
//   const [selectedFile, setSelectedFile] = useState(null);
//   // Données provenant de l’API
//   const [typeDepenses, setTypeDepenses] = useState([]);
//   const [natureDepenses, setNatureDepenses] = useState([]);
//   const [TauxKilometrage, setTauxKilometrage] = useState({});
//   // URL du fichier sélectionné
//   const fileUrl = selectedFile ? URL.createObjectURL(selectedFile) : "";
//   // Gestion des erreurs d’upload de fichiers
//   const [fileUploadError, setFileUploadError] = useState({});

//   // Initialisation de Lightbox
//   useEffect(() => {
//     initLightboxJS("Insert_Your_License_Key_Or_Use_Default", "default");
//   }, []);

//   // Synchronisation des dépenses locales avec formData
//   useEffect(() => {
//     setDepenses(formData.depenses);
//   }, [formData.depenses]);

//   // Récupération des taux de kilométrage depuis l’API
//   useEffect(() => {
//     fetch(`${process.env.REACT_APP_API_BASE_URL}/TauxKilometrage`)
//       .then((response) => response.json())
//       .then((data) => setTauxKilometrage(data))
//       .catch((error) => console.error("Error fetching funds:", error));
//   }, []);

//   /**
//    * Met à jour un champ d’une dépense
//    */
//   const handleDepenseChange = (depenseId, field, value) => {
//     const newDepenses = [...depenses];
//     const depense = newDepenses.find((d) => d.id === depenseId);
//     if (depense) {
//       depense[field] = value;
//       setDepenses(newDepenses);
//     }
//   };

//   /**
//    * Met à jour un champ d’une ligne de dépense
//    */
//   const handleRowChange = (depenseId, rowId, field, value) => {
//     const newDepenses = [...depenses];
//     const depense = newDepenses.find((d) => d.id === depenseId);
//     const row = depense?.rows.find((r) => r.rowId === rowId);
//     if (row) {
//       row[field] = value;
//       // Réinitialisation si ce n’est pas du kilométrage
//       if (field === "typeDepenses" && value !== "Kilométrage") {
//         row.kms = null; // Reset kms to null if typeDepenses is not "Kilométrage"
//         row.montantRembourser = null;
//         row.montant = null;
//       }
//       // Paramétrage automatique pour le kilométrage
//       if (field === "typeDepenses" && value === "Kilométrage") {
//         row.fournisseur = ""; // Reset kms to null if typeDepenses is not "Kilométrage"
//         row.montant = null;
//         row.montantRembourser = null;
//         row.natureDepenses = "Kilométrage - déplacement";
//         row.compte = 36200;
//         row.codeTaxe = "AA";
//       }

//       if (field === "montant" && value === "Kilométrage") {
//         row.fournisseur = ""; // Reset kms to null if typeDepenses is not "Kilométrage"
//         row.montant = null;
//         row.montantRembourser = null;
//         row.natureDepenses = "Kilométrage - déplacement";
//         row.compte = 36200;
//         row.codeTaxe = "AA";
//       }
//       // Calcul automatique du montant basé sur les kilomètres
//       if (field === "kms") {
//         // Créer une date sans l'heure pour la comparaison (utilisation de toISOString pour récupérer uniquement la date)
//         const reclamationDate = new Date(depense.dateDepense)
//           .toISOString()
//           .split("T")[0]; // "YYYY-MM-DD"

//         // Chercher un taux de kilométrage où la date de la réclamation est entre Datedebut et DatedeFin (sans heure)
//         const tauxKilometrage = TauxKilometrage.find((t) => {
//           const startDate = new Date(t.Datedebut).toISOString().split("T")[0]; // "YYYY-MM-DD"
//           const endDate = t.DatedeFin
//             ? new Date(t.DatedeFin).toISOString().split("T")[0]
//             : null; // "YYYY-MM-DD" ou null si non défini

//           // Vérifier si la date de réclamation est après la startDate, et avant endDate si elle existe
//           return (
//             reclamationDate >= startDate &&
//             (endDate ? reclamationDate <= endDate : true)
//           );
//         });

//         if (tauxKilometrage) {
//           row.montant = parseFloat(row.kms * tauxKilometrage.value).toFixed(2); // Calculer le montant si la période de réclamation est dans l'intervalle
//         }
//       }
//       setDepenses(newDepenses);
//       setFormData({ ...formData, depenses: newDepenses });
//     }
//   };

//   /**
//    * Ajoute une nouvelle ligne à une dépense
//    */
//   const addRow = (depenseId) => {
//     const newDepenses = [...depenses];
//     const depense = newDepenses.find((d) => d.id === depenseId);
//     if (depense) {
//       depense.rows.push({
//         rowId: uuidv4(),
//         typeDepenses: "",
//         kms: null,
//         fournisseur: "",
//         natureDepenses: "",
//         compte: null,
//         codeTaxe: "",
//         funds: formData.funds,
//         centreCouts: formData.centreCout,
//         ordre: formData.ordre,
//         montant: null,
//         montantRembourser: null,
//         commentaire: "",
//       });

//       setDepenses(newDepenses);
//       setFormData({ ...formData, depenses: newDepenses });
//     }
//   };

//   /**
//    * Supprime une ligne d’une dépense
//    */
//   const removeRow = (depenseId, rowId) => {
//     const newDepenses = [...depenses];
//     const depense = newDepenses.find((d) => d.id === depenseId);
//     if (depense) {
//       depense.rows = depense.rows.filter((row) => row.rowId !== rowId);
//       setDepenses(newDepenses);
//       setFormData({ ...formData, depenses: newDepenses });
//     }
//   };

//   /**
//    * Ajoute une nouvelle dépense complète
//    */
//   const addDepense = () => {
//     const newDepenses = [
//       ...depenses,
//       {
//         id: uuidv4(),
//         dateDepense: "",
//         descriptionDepense: "",
//         deplacement: "",
//         trajetFiles: [],
//         rows: [
//           {
//             rowId: uuidv4(),
//             typeDepenses: "",
//             kms: null,
//             fournisseur: "",
//             natureDepenses: "",
//             compte: null,
//             codeTaxe: "",
//             funds: formData.funds,
//             centreCouts: formData.centreCout,
//             ordre: formData.ordre,
//             montant: null,
//             montantRembourser: null,
//             commentaire: "",
//           },
//         ],
//         tousReçusFournis: "oui",
//         files: [],
//       },
//     ];
//     setDepenses(newDepenses);
//     setFormData({ ...formData, depenses: newDepenses });
//   };

//   /**
//    * Supprime une dépense complète
//    */
//   const removeDepense = (depenseId) => {
//     const newDepenses = depenses.filter((d) => d.id !== depenseId);
//     setDepenses(newDepenses);
//     setFormData({ ...formData, depenses: newDepenses });
//   };

//   const handleFileUpload = async (e, depenseId, type = "facture") => {
//     const MAX_FILE_SIZE_MB = 5;
//     const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
//     const files = Array.from(e.target.files) || [];

//     const errorKey = `${depenseId}_${type}`;

//     const ALLOWED_TYPES = [
//       "application/pdf",
//       "image/jpeg",
//       "image/jpg",
//       "image/png",
//     ];

//     const invalidFiles = files.filter(
//       (file) => !ALLOWED_TYPES.includes(file.type),
//     );

//     if (invalidFiles.length > 0) {
//       setFileUploadError((prev) => ({
//         ...prev,
//         [errorKey]: "Formats acceptés : PDF, JPG, JPEG, PNG.",
//       }));
//       return;
//     }

//     // Resize images
//     const resizeImage = (file) =>
//       new Promise((resolve) => {
//         if (!file.type.includes("image")) return resolve(file); // Skip non-images

//         Resizer.imageFileResizer(
//           file,
//           1600, // maxWidth
//           1600, // maxHeight
//           "WEBP", // format de sortie
//           70, // qualité (70% pour captures écrans)
//           0, // rotation
//           (uri) => {
//             const resizedFile = new File([uri], file.name, {
//               type: "image/webp",
//             });
//             resolve(resizedFile);
//           },
//           "blob",
//           200, // minWidth
//           200, // minHeight
//         );
//       });

//     const compressPDF = async (file) => {
//       // Vérifie que c’est bien un PDF
//       if (file.type !== "application/pdf") return file;

//       try {
//         const arrayBuffer = await file.arrayBuffer();

//         // Tente de charger le PDF (même s’il est chiffré)
//         const pdfDoc = await PDFDocument.load(arrayBuffer, {
//           ignoreEncryption: true,
//         });

//         // Vérifie si le PDF contient une signature (à éviter de modifier)
//         const hasSignature = pdfDoc.context
//           .lookup(pdfDoc.catalog?.acroForm)
//           ?.has("SigFlags");

//         if (hasSignature) {
//           console.warn(
//             "PDF signé détecté — compression ignorée pour préserver la signature.",
//           );
//           return file; // on retourne le fichier original non modifié
//         }

//         // Sinon, compression légère
//         const compressedBytes = await pdfDoc.save();
//         return new File([compressedBytes], file.name, {
//           type: "application/pdf",
//         });
//       } catch (error) {
//         console.error("Erreur lors du traitement du PDF :", error);
//         // Si erreur (PDF illisible ou signé), on retourne le fichier original
//         return file;
//       }
//     };

//     // Traitement par type
//     const processFile = async (file) => {
//       if (file.type.includes("image")) {
//         return await resizeImage(file);
//       } else if (file.type === "application/pdf") {
//         return await compressPDF(file);
//       }
//       return file;
//     };

//     const processedFiles = await Promise.all(files.map(processFile));

//     // Vérifier après traitement si certains fichiers sont trop gros
//     const oversizedFiles = processedFiles.filter(
//       (file) => file.size > MAX_FILE_SIZE,
//     );
//     if (oversizedFiles.length > 0) {
//       setFileUploadError((prev) => ({
//         ...prev,
//         [errorKey]: `Un ou plusieurs fichiers (même après compression) dépassent ${MAX_FILE_SIZE_MB} Mo.`,
//       }));
//       return;
//     }

//     setFileUploadError((prev) => ({
//       ...prev,
//       [errorKey]: "",
//     }));

//     const newDepenses = [...depenses];
//     const depense = newDepenses.find((d) => d.id === depenseId);

//     if (depense) {
//       // const existingFiles = depense.files || [];
//       const existingFiles =
//         type === "trajet" ? depense.trajetFiles || [] : depense.files || [];
//       // Récupère les noms existants
//       const existingFileNames = new Set(
//         existingFiles.map((f) => f.name || f.filename),
//       );

//       // Garde uniquement les fichiers dont le nom n'existe pas déjà
//       const filteredFiles = processedFiles.filter(
//         (file) => !existingFileNames.has(file.name),
//       );

//       if (filteredFiles.length < processedFiles.length) {
//         setFileUploadError((prev) => ({
//           ...prev,
//           [errorKey]: "Un fichier avec ce nom existe déjà.",
//         }));
//       } else {
//         setFileUploadError((prev) => ({
//           ...prev,
//           [errorKey]: "",
//         }));
//       }

//       const updatedFiles = [...existingFiles, ...filteredFiles];
//       // depense.files = updatedFiles;
//       if (type === "trajet") {
//         depense.trajetFiles = updatedFiles;
//       } else {
//         depense.files = updatedFiles;
//       }
//       const newPreviewUrls = { ...previewUrls };
//       filteredFiles.forEach((file) => {
//         newPreviewUrls[file.name] = URL.createObjectURL(file);
//       });

//       setDepenses(newDepenses);
//       setPreviewUrls(newPreviewUrls);
//       setFormData({ ...formData, depenses: newDepenses });
//     }
//   };

//   const handleFileDelete = (fileName, depenseId, type = "facture") => {
//     const newDepenses = [...depenses]; // Copiez les dépenses existantes

//     // Trouver la dépense par son id
//     const depense = newDepenses.find((d) => d.id === depenseId);

//     if (depense) {
//       // const updatedFiles = depense.files.filter(
//       //   (file) => file.name !== fileName
//       // );
//       const updatedFiles = (
//         type === "trajet" ? depense.trajetFiles : depense.files
//       ).filter((file) => file.name !== fileName);
//       // Supprimer le fichier de la dépense spécifique
//       if (type === "trajet") {
//         depense.trajetFiles = updatedFiles;
//       } else {
//         depense.files = updatedFiles;
//       }
//       // depense.files = updatedFiles;

//       const updatedPreviewUrls = { ...previewUrls };
//       delete updatedPreviewUrls[fileName]; // Supprimer l'URL de prévisualisation du fichier supprimé

//       setDepenses(newDepenses); // Mettez à jour l'état des dépenses
//       setPreviewUrls(updatedPreviewUrls); // Mettez à jour les URLs de prévisualisation
//       setFormData({ ...formData, depenses: newDepenses }); // Mettez à jour formData
//     }
//   };

//   // Récupération des types et natures de dépenses
//   useEffect(() => {
//     fetch(`${process.env.REACT_APP_API_BASE_URL}/TypeDepense`)
//       .then((response) => response.json())
//       .then((data) => setTypeDepenses(data))
//       .catch((error) => console.error("Error fetching TypeDepense:", error));
//     fetch(`${process.env.REACT_APP_API_BASE_URL}/NatureDepense`)
//       .then((response) => response.json())
//       .then((data) => setNatureDepenses(data))
//       .catch((error) => console.error("Error fetching NatureDepense:", error));
//   }, []);

//   return (
//     <div className="mt-6">
//       {depenses.map((depense, index) => (
//         <div
//           key={depense.id}
//           className="mb-6 border p-4 rounded-lg shadow-md bg-gray-50 "
//         >
          
  
//   <h3 className="font-semibold text-xl text-gray-900 tracking-tight">
//     Dépense {index + 1}
//   </h3>


//           {/* Date and Description for Each Depense */}
//           <div className="mt-5 w-3/4 grid grid-cols-2 gap-4">
//             <div className="w-3/5">
//               <label className="block text-sm font-medium text-blue-900">
//                 Date <span className="text-xl text-red-900">*</span> :
//               </label>
//               <input
//                 type="date"
//                 min={
//                   formData.periodReclamation
//                     ? `${formData.periodReclamation.split("-")[0]}-${
//                         formData.periodReclamation.split("-")[1]
//                       }-01`
//                     : ""
//                 }
//                 max={
//                   formData.periodReclamation
//                     ? (() => {
//                         const [year, month] =
//                           formData.periodReclamation.split("-");
//                         return new Date(year, month, 0)
//                           .toISOString()
//                           .split("T")[0]; // Dernier jour du mois
//                       })()
//                     : ""
//                 }
//                 value={depense.dateDepense}
//                 onChange={(e) =>
//                   handleDepenseChange(depense.id, "dateDepense", e.target.value)
//                 }
//                 className="mt-1 w-full p-3 border border-gray-200 rounded-xl  shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200"
//                 readOnly={isReadOnly}
//               />
//               {renderError(`dateDepense_${depense.id}`)}
//             </div>

//             <div className="w-3/5">
//               <label className="block text-sm font-medium text-blue-900">
//                 Description du déplacement ou de la dépense{" "}
//                 <span className="text-xl text-red-900">*</span> :
//               </label>
//               <textarea
//                 placeholder="Ajoutez une description..."
//                 className="mt-1 w-full p-3 border border-gray-200 rounded-xl  shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200"
//                 rows="2"
//                 maxLength={50} // Limitation à 50 caractères
//                 value={depense.descriptionDepense}
//                 onChange={(e) =>
//                   handleDepenseChange(
//                     depense.id,
//                     "descriptionDepense",
//                     e.target.value,
//                   )
//                 }
//                 readOnly={isReadOnly}
//               ></textarea>
//               {renderError(`descriptionDepense_${depense.id}`)}
//             </div>
//             {depense.rows.some((row) => row.typeDepenses === "Kilométrage") && (
//               <div className="w-3/5">
//                 <label className="block text-sm font-medium text-blue-900">
//                   Point de départ-Destination-Point de retour{" "}
//                   <span className="text-xl text-red-900">*</span> :
//                 </label>
//                 <textarea
//                   placeholder=""
//                   className="mt-1 w-full p-3 border border-gray-200 rounded-xl  shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200"
//                   rows="2"
//                   value={depense.deplacement}
//                   onChange={(e) =>
//                     handleDepenseChange(
//                       depense.id,
//                       "deplacement",
//                       e.target.value,
//                     )
//                   }
//                   disabled={isReadOnly}
//                 ></textarea>
//                 {renderError(`deplacement_${depense.id}`)}
//               </div>
//             )}
//             {depense.rows.some((row) => row.typeDepenses === "Kilométrage") && (
//               <div className="w-3/5">
//                 <label
//                   htmlFor="fileUpload"
//                   className="block text-sm font-medium text-blue-900 mb-2"
//                 >
//                   Veuillez joindre une capture du trajet{" "}:
//                   {/* <span className="text-xl text-red-900">*</span> : */}
//                 </label>
//                 {/* {isReadOnly===false &&( */}
//                 <div>
//                   <div className="relative">
//                     <input
//                       type="file"
//                       id="fileUpload"
//                       name="fileUpload"
//                       accept=".pdf,.jpg,.jpeg,.png"
//                       multiple // Permet l'upload multiple
//                       onChange={(e) =>
//                         handleFileUpload(e, depense.id, "trajet")
//                       }
//                       className="absolute inset-0 opacity-0 cursor-pointer"
//                       // disabled={isReadOnly}
//                     />
//                     <button className="w-full px-4 py-2 border border-blue-500 rounded-lg text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
//                       Choisir des fichiers
//                     </button>
//                   </div>
//                   <p className="text-sm text-gray-500 mt-1">
//                     Formats acceptés : PDF, JPG, JPEG, PNG.
//                   </p>
//                   {renderError(`trajetFiles_${depense.id}`)}
//                   {fileUploadError[`${depense.id}_trajet`] && (
//                     <p style={{ color: "red", marginTop: "0.5rem" }}>
//                       {fileUploadError[`${depense.id}_trajet`]}
//                     </p>
//                   )}
//                 </div>
//                 {/* )} */}
                
//                 {depense.trajetFiles && depense.trajetFiles.length > 0 && (
//                   <div className="mt-3 space-y-2">
//                     {depense.trajetFiles
//                       ?.filter((f) => f !== null && f !== undefined)
//                       .map((file) => {
//                         const filename = file.name || file.filename || "";
//                         const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
//                           filename,
//                         );
//                         const isPdf = /\.pdf$/i.test(filename);

//                         return (
//                           <div
//                             key={file._id || filename}
//                             className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-white shadow-sm hover:bg-blue-50 transition-colors duration-150"
//                           >
//                             {/* Icône selon le type */}
//                             <span className="text-lg flex-shrink-0">
//                               {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
//                             </span>

//                             {/* Nom + sous-titre cliquable */}
//                             <button
//                               onClick={() => {
//                                 setSelectedFile(file);
//                                 setShowPopup(true);
//                               }}
//                               className="flex-1 min-w-0 text-left group"
//                             >
//                               <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
//                                 {filename}
//                               </p>
//                               <p className="text-xs text-gray-400">
//                                 Cliquer pour visualiser
//                               </p>
//                             </button>

//                             {/* Flèche d'ouverture */}
//                             <span className="text-blue-400 text-xs flex-shrink-0">
//                               ↗
//                             </span>

//                             {/* Bouton supprimer */}
//                             {(!file._id ||
//                               formData.etatDemande === "Brouillon" ||
//                               (page !== "Mes taches" &&
//                                 formData.etatDemande === "Rejeté")) && (
//                               <button
//                                 onClick={() =>
//                                   handleFileDelete(
//                                     filename,
//                                     depense.id,
//                                     "trajet",
//                                   )
//                                 }
//                                 className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
//                               >
//                                 <TrashIcon className="w-5 h-5" />
//                               </button>
//                             )}
//                           </div>
//                         );
//                       })}
//                   </div>
//                 )}

                
//               </div>
//             )}
//           </div>

//           {/* Table for Rows */}
//           <div className="mt-10">
//             {isReadOnly === false && (
//               <button
//                 type="button"
//                 className="mt-5 bg-blue-500 text-white px-5 py-2 rounded-full text-sm font-medium
//                  hover:bg-blue-600 
//                  active:scale-95 
//                  transition-all duration-150"
//                 onClick={() => addRow(depense.id)}
//               >
//                 + Ajouter une dépense
//               </button>
//             )}
//             <table className="w-full border border-gray-900 mt-2">
              
              
//               <thead className="bg-blue-100">
//                 <tr>
//                   <th className="border border-gray-300 px-10 py-2">
//                     Type de dépense
//                   </th>
//                   <th className="border border-gray-300 px-2 py-1 w-[7%]">
//                     Kms
//                   </th>
//                   <th className="border border-gray-300 px-2 py-2">
//                     Fournisseur
//                   </th>
//                   <th className="border border-gray-300 px-20 py-2">
//                     Nature de la dépense
//                   </th>
//                   <th className="border border-gray-300 px-2 py-2">Compte</th>
//                   <th className="border border-gray-300 px-5 py-2">Fonds</th>
//                   <th className="border border-gray-300 px-8 py-2">
//                     Centre de coût
//                   </th>
//                   <th className="border border-gray-300 px-3 py-2">
//                     Ordre (optionnel)
//                   </th>
//                   <th className="border border-gray-300 px-4 py-2 w-[6%]">
//                     Montant ($)
//                   </th>
//                   <th className="border border-gray-300 px-3 py-2 w-[6%]">
//                     Montant à rembourser au Conseil ($)
//                   </th>
//                   <th className="border border-gray-300 px-4 py-2 w-[12%]">
//                     Commentaires
//                   </th>
//                   <th className="border border-gray-300 px-1 py-1"></th>
//                 </tr>
//               </thead> 
//               <tbody className="w-full border border-gray-900 mt-2">
//                 {depense.rows.map((row) => (
//                   <tr key={row.rowId}>
//                     <td className="border p-2">
//                       <select
//                         id="typeDepenses"
//                         name="typeDepenses"
//                         value={row.typeDepenses}
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "typeDepenses",
//                             e.target.value,
//                           )
//                         }
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         disabled={isReadOnly}
//                       >
//                         <option value="">Sélectionner</option>
//                         {typeDepenses.map((typeDepense) => (
//                           <option
//                             key={typeDepense._id}
//                             value={typeDepense.value}
//                           >
//                             {typeDepense.value}
//                           </option>
//                         ))}
//                       </select>
//                       {renderError(`typeDepenses_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2 w-[6%]">
//                       <input
//                         type="text"
//                         value={row.kms || ""}
//                         onChange={(e) => {
//                           const value = e.target.value;
//                           // Accepte chiffres + virgule + point
//                           if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
//                             handleRowChange(
//                               depense.id,
//                               row.rowId,
//                               "kms",
//                               value,
//                             );
//                           }
//                         }}
//                         onKeyDown={(e) => {
//                           // Autoriser uniquement les touches numériques, backspace, delete, flèches
//                           if (
//                             !(
//                               (e.key >= "0" && e.key <= "9") ||
//                               e.key === "," ||
//                               e.key === "." ||
//                               e.key === "Backspace" ||
//                               e.key === "Delete" ||
//                               e.key === "ArrowLeft" ||
//                               e.key === "ArrowRight" ||
//                               e.key === "Tab"
//                             )
//                           ) {
//                             e.preventDefault();
//                           }
//                         }}
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         required
//                         disabled={
//                           row.typeDepenses !== "Kilométrage" ||
//                           depense.dateDepense.trim() === ""
//                         }
//                         readOnly={
//                           isReadOnly &&
//                           page === "Mes taches" &&
//                           (formData.etatDemande === "Rejeté" ||
//                             formData.etatDemande === "Approuvé")
//                         }
//                       />
//                       {depense.dateDepense.trim() === "" &&
//                         row.typeDepenses === "Kilométrage" && (
//                           <p className="text-red-600 text-sm mt-1">
//                             Veuillez remplir la date de dépense avant d’ajouter
//                             les kilomètres.
//                           </p>
//                         )}
//                       {renderError(`kms_${row.rowId}`)}
//                     </td>
//                     <td className="border p-2">
//                       <input
//                         type="text"
//                         value={row.fournisseur}
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "fournisseur",
//                             e.target.value,
//                           )
//                         }
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         disabled={row.typeDepenses === "Kilométrage"}
//                         readOnly={isReadOnly}
//                       />
//                       {renderError(`fournisseur_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <select
//                         id="natureDepenses"
//                         name="natureDepenses"
//                         value={row.natureDepenses}
//                         disabled={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                         onChange={(e) => {
//                           const selectedNature = e.target.value;
//                           const selectedCompte =
//                             natureDepenses.find(
//                               (nature) => nature.value === selectedNature,
//                             )?.compte || "";
//                           const selectedcodeTaxe =
//                             natureDepenses.find(
//                               (nature) => nature.value === selectedNature,
//                             )?.codeTaxe || "";

//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "natureDepenses",
//                             selectedNature,
//                           );
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "compte",
//                             selectedCompte,
//                           );
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "codeTaxe",
//                             selectedcodeTaxe,
//                           );
//                         }}
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                       >
//                         <option value="">Sélectionner</option>

//                         {(row.typeDepenses === "Kilométrage"
//                           ? natureDepenses.filter((nature) =>
//                               [
//                                 "Kilométrage - déplacement",
//                                 "Kilométrage - formation enseignant",
//                                 "Kilométrage - formation non-enseignant",
//                               ].includes(nature.value),
//                             )
//                           : natureDepenses.filter((nature) =>
//                               row.typeDepenses === "Visa du Conseil"
//                                 ? nature.value !==
//                                   "Hébergement - famille/ami (25$)"
//                                 : true,
//                             )
//                         ).map((natureDepense) => (
//                           <option
//                             key={natureDepense.value}
//                             value={natureDepense.value}
//                           >
//                             {natureDepense.value}
//                           </option>
//                         ))}
//                       </select>

//                       {renderError(`natureDepenses_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <input
//                         type="text"
//                         value={row.compte}
//                         // disabled={row.typeDepenses === "Kilométrage"}
//                         readOnly={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "compte",
//                             e.target.value,
//                           )
//                         }
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                       />
//                       {renderError(`compte_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <select
//                         id="funds"
//                         name="funds"
//                         value={row.funds}
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "funds",
//                             e.target.value,
//                           )
//                         }
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         disabled={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                       >
//                         <option value="">Sélectionner</option>
//                         {funds.map((fund) => (
//                           <option key={fund._id} value={fund.value}>
//                             {fund.value}
//                           </option>
//                         ))}
//                       </select>
//                       {renderError(`funds_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <select
//                         id="centreCouts"
//                         name="centreCouts"
//                         value={row.centreCouts}
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "centreCouts",
//                             e.target.value,
//                           )
//                         }
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         disabled={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                       >
//                         <option value="">Sélectionner</option>
//                         {centreCouts.map((centreCout) => (
//                           <option key={centreCout._id} value={centreCout.value}>
//                             {centreCout.value}
//                           </option>
//                         ))}
//                       </select>
//                       {renderError(`centreCouts_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <input
//                         type="text"
//                         value={row.ordre}
//                         maxLength={6}
//                         onChange={(e) => {
//                           const value = e.target.value;
//                           // Accepte seulement les chiffres (0-9)
//                           if (/^\d*$/.test(value)) {
//                             handleRowChange(
//                               depense.id,
//                               row.rowId,
//                               "ordre",
//                               value,
//                             );
//                           }
//                         }}
//                         onKeyDown={(e) => {
//                           // Autoriser uniquement les touches numériques, backspace, delete, flèches
//                           if (
//                             !(
//                               (e.key >= "0" && e.key <= "9") ||
//                               e.key === "Backspace" ||
//                               e.key === "Delete" ||
//                               e.key === "ArrowLeft" ||
//                               e.key === "ArrowRight" ||
//                               e.key === "Tab"
//                             )
//                           ) {
//                             e.preventDefault();
//                           }
//                         }}
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         disabled={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                       />
//                     </td>

//                     <td className="border p-2 w-[6%]">
//                       <div className="relative">
//                         <input
//                           type="text"
//                           step="0.01"
//                           value={row.montant ?? ""}
//                           disabled={
//                             row.typeDepenses === "Kilométrage" ||
//                             (isReadOnly &&
//                               !(
//                                 page === "Mes taches" &&
//                                 formData.etatDemande === "En traitement"
//                               ))
//                           }
//                           onBlur={(e) => {
//                             const rawValue = e.target.value.replace(",", "."); // conversion
//                             const value = parseFloat(rawValue || 0).toFixed(2);

//                             handleRowChange(
//                               depense.id,
//                               row.rowId,
//                               "montant",
//                               value,
//                             );

//                             if (
//                               row.typeDepenses === "Visa du Conseil" &&
//                               row.natureDepenses ===
//                                 "Dépense personnelle - Visa"
//                             ) {
//                               handleRowChange(
//                                 depense.id,
//                                 row.rowId,
//                                 "montantRembourser",
//                                 value,
//                               );
//                             }
//                           }}
//                           onChange={(e) => {
//                             const value = e.target.value;
//                             // Accepte chiffres + virgule + point
//                             if (/^-?[0-9]*[.,]?[0-9]*$/.test(value)) {
//                               handleRowChange(
//                                 depense.id,
//                                 row.rowId,
//                                 "montant",
//                                 value.replace(",", "."), // conversion aussi ici
//                               );
//                             }
//                           }}
//                           //   onBlur={(e) => {
//                           //     const value = parseFloat(e.target.value || 0).toFixed(2);
//                           //     handleRowChange(depense.id, row.rowId, "montant", value);

//                           //     if (
//                           //       row.typeDepenses === "Visa du Conseil" &&
//                           //       row.natureDepenses === "Dépense personnelle - Visa"
//                           //     ) {
//                           //       handleRowChange(depense.id, row.rowId, "montantRembourser", value);
//                           //     }
//                           //   }}

//                           //   onChange={(e) => {
//                           //   const value = e.target.value;
//                           //   // Accepte chiffres + virgule + point
//                           //   if (/^[0-9]*[.,-]?[0-9]*$/.test(value)) {
//                           //     handleRowChange(depense.id, row.rowId, "montant", value);
//                           //   }
//                           // }}
//                           onKeyDown={(e) => {
//                             // Autoriser uniquement les touches numériques, backspace, delete, flèches
//                             if (
//                               !(
//                                 (e.key >= "0" && e.key <= "9") ||
//                                 e.key === "," ||
//                                 e.key === "." ||
//                                 e.key === "-" ||
//                                 e.key === "Backspace" ||
//                                 e.key === "Delete" ||
//                                 e.key === "ArrowLeft" ||
//                                 e.key === "ArrowRight" ||
//                                 e.key === "Tab"
//                               )
//                             ) {
//                               e.preventDefault();
//                             }
//                           }}
//                           className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         />
//                         <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
//                           $
//                         </span>
//                       </div>
//                       {renderError(`montant_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2 w-[6%]">
//                       <div className="relative">
//                         <input
//                           type="text"
//                           step="0.01"
//                           value={row.montantRembourser ?? ""}
//                           disabled={
//                             row.typeDepenses === "Kilométrage" ||
//                             row.typeDepenses === "Autre dépense" ||
//                             (row.typeDepenses === "Visa du Conseil" &&
//                               row.natureDepenses ===
//                                 "Dépense personnelle - Visa") ||
//                             (isReadOnly &&
//                               !(
//                                 page === "Mes taches" &&
//                                 formData.etatDemande === "En traitement"
//                               ))
//                           }
//                           onBlur={(e) => {
//                             let value = e.target.value.replace(",", "."); // remplace la virgule par un point
//                             handleRowChange(
//                               depense.id,
//                               row.rowId,
//                               "montantRembourser",
//                               parseFloat(value || 0).toFixed(2),
//                             );
//                           }}
//                           onChange={(e) => {
//                             const value = e.target.value.replace(",", "."); // idem ici
//                             // Accepte chiffres + virgule + point
//                             if (/^-?[0-9]*[.,]?[0-9]*$/.test(value)) {
//                               handleRowChange(
//                                 depense.id,
//                                 row.rowId,
//                                 "montantRembourser",
//                                 value,
//                               );
//                             }
//                           }}
//                           onKeyDown={(e) => {
//                             // Autoriser uniquement les touches numériques, backspace, delete, flèches
//                             if (
//                               !(
//                                 (e.key >= "0" && e.key <= "9") ||
//                                 e.key === "," ||
//                                 e.key === "." ||
//                                 e.key === "-" ||
//                                 e.key === "Backspace" ||
//                                 e.key === "Delete" ||
//                                 e.key === "ArrowLeft" ||
//                                 e.key === "ArrowRight" ||
//                                 e.key === "Tab"
//                               )
//                             ) {
//                               e.preventDefault();
//                             }
//                           }}
//                           //   onBlur={(e) =>
//                           //     handleRowChange(
//                           //       depense.id,
//                           //       row.rowId,
//                           //       "montantRembourser",
//                           //       parseFloat(e.target.value || 0).toFixed(2)
//                           //     )
//                           //   }
//                           //   onChange={(e) => {
//                           //   const value = e.target.value;
//                           //   // Accepte chiffres + virgule + point
//                           //   if (/^[0-9]*[.,-]?[0-9]*$/.test(value)) {
//                           //     handleRowChange(depense.id, row.rowId, "montantRembourser", value);
//                           //   }
//                           // }}

//                           className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         />
//                         <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
//                           $
//                         </span>
//                       </div>
//                       {renderError(`montantRembourser_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       <textarea
//                         placeholder="Ajoutez un commentaire..."
//                         className="mt-1 p-2 w-full border border-gray-400 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
//                         rows="2"
//                         value={row.commentaire}
//                         onChange={(e) =>
//                           handleRowChange(
//                             depense.id,
//                             row.rowId,
//                             "commentaire",
//                             e.target.value,
//                           )
//                         }
//                         disabled={
//                           isReadOnly &&
//                           !(
//                             page === "Mes taches" &&
//                             formData.etatDemande === "En traitement"
//                           )
//                         }
//                       ></textarea>
//                       {renderError(`commentaire_${row.rowId}`)}
//                     </td>

//                     <td className="border p-2">
//                       {depense.rows.length > 1 && isReadOnly === false && (
//                         <button
//                           // className=" px-2 py-1 rounded hover:bg-red-600"
//                           className="w-8 h-10 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200"
//                           onClick={() => removeRow(depense.id, row.rowId)}
//                         >
//                           <TrashIcon className="w-7 h-7" />
//                         </button>
//                       )}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>

//             <div className="mt-10 w-3/4 grid grid-cols-2 gap-0">
//               <div className="w-3/5">
//                 <label
//                   htmlFor={`tousReçusFournis-${depense.id}`}
//                   className="block text-sm font-semibold text-blue-900 mb-1"
//                 >
//                   Avez-vous tous les reçus pour les dépenses ci-dessus :{" "}
//                   <span className="text-red-500">*</span>
//                 </label>

//                 <div className="mt-2 flex space-x-3">
//                   {["oui", "non"].map((option) => {
//                     const isSelected = depense.tousReçusFournis === option;
//                     return (
//                       <label
//                         key={option}
//                         // className={`cursor-pointer px-3 py-1 text-sm rounded-md border transition-colors duration-150
//                         // ${isSelected
//                         //     ? "bg-blue-600 text-white border-blue-600"
//                         //     : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
//                         //   }`}
//                         className={`
//                   flex items-center justify-center w-[80px]
//                   px-4 py-2 rounded-lg cursor-pointer
//                   text-sm font-medium
//                   border transition-all duration-200
//                   focus-within:ring-2 focus-within:ring-blue-300 focus-within:ring-offset-1
//                   ${
//                     isSelected
//                       ? "bg-blue-100 text-blue-600 border-blue-300"
//                       : "bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-600"
//                   }
//                 `}
//                       >
//                         <input
//                           type="radio"
//                           name={`tousReçusFournis-${depense.id}`}
//                           value={option}
//                           onChange={(e) =>
//                             handleDepenseChange(
//                               depense.id,
//                               "tousReçusFournis",
//                               e.target.value,
//                             )
//                           }
//                           className="hidden"
//                           disabled={isReadOnly}
//                         />
//                         {option.charAt(0).toUpperCase() + option.slice(1)}
//                       </label>
//                     );
//                   })}
//                 </div>
//               </div>

//               <div className="w-3/5">
//                 <label
//                   htmlFor="fileUpload"
//                   className="block text-sm font-medium text-blue-900 mb-2"
//                 >
//                   Veuillez joindre une copie de vos factures{" "}
//                   {depense.rows.some(
//                     (row) =>
//                       row.typeDepenses !== "Kilométrage" &&
//                       !(row.natureDepenses === "Dépense personnelle - Visa") &&
//                       !(
//                         row.natureDepenses === "Hébergement - famille/ami (25$)"
//                       ) &&
//                       !(depense.tousReçusFournis === "non") &&
//                       !(row.natureDepenses === "407 ETR"),
//                   ) && <span className="text-xl text-red-900">*</span>}{" "}
//                   :
//                 </label>
//                 {/* {isReadOnly===false &&( */}
//                 <div>
//                   <div className="relative">
//                     <input
//                       type="file"
//                       id="fileUpload"
//                       name="fileUpload"
//                       accept=".pdf,.jpg,.jpeg,.png"
//                       multiple // Permet l'upload multiple
//                       onChange={(e) => handleFileUpload(e, depense.id)}
//                       className="absolute inset-0 opacity-0 cursor-pointer"
//                       // disabled={isReadOnly}
//                     />
//                     <button className="w-full px-4 py-2 border border-blue-500 rounded-lg text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
//                       Choisir des fichiers
//                     </button>
//                   </div>
//                   <p className="text-sm text-gray-500 mt-1">
//                     Formats acceptés : PDF, JPG, JPEG, PNG.
//                   </p>
//                   {renderError(`files_${depense.id}`)}
//                   {fileUploadError[`${depense.id}_facture`] && (
//                     <p style={{ color: "red", marginTop: "0.5rem" }}>
//                       {fileUploadError[`${depense.id}_facture`]}
//                     </p>
//                   )}
//                 </div>
//                 {/* )} */}
//                 {depense.files && depense.files.length > 0 && (
//                   <div className="mt-3 space-y-2">
//                     {depense.files
//                       ?.filter((f) => f !== null && f !== undefined)
//                       .map((file) => {
//                         const filename = file.name || file.filename || "";
//                         const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
//                           filename,
//                         );
//                         const isPdf = /\.pdf$/i.test(filename);

//                         return (
//                           <div
//                             key={file._id || filename}
//                             className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-white shadow-sm hover:bg-blue-50 transition-colors duration-150"
//                           >
//                             {/* Icône selon le type */}
//                             <span className="text-lg flex-shrink-0">
//                               {isImage ? "🖼️" : isPdf ? "📄" : "📎"}
//                             </span>

//                             {/* Nom + sous-titre cliquable */}
//                             <button
//                               onClick={() => {
//                                 setSelectedFile(file);
//                                 setShowPopup(true);
//                               }}
//                               className="flex-1 min-w-0 text-left group"
//                             >
//                               <p className="text-sm font-medium text-blue-800 group-hover:text-blue-600 truncate">
//                                 {filename}
//                               </p>
//                               <p className="text-xs text-gray-400">
//                                 Cliquer pour visualiser
//                               </p>
//                             </button>

//                             {/* Flèche d'ouverture */}
//                             <span className="text-blue-400 text-xs flex-shrink-0">
//                               ↗
//                             </span>

//                             {/* Bouton supprimer */}
//                             {(!file._id ||
//                               formData.etatDemande === "Brouillon" ||
//                               (page !== "Mes taches" &&
//                                 formData.etatDemande === "Rejeté")) && (
//                               <button
//                                 onClick={() =>
//                                   handleFileDelete(
//                                     filename,
//                                     depense.id,
//                                     "facture",
//                                   )
//                                 }
//                                 className="w-9 h-9 border border-red-500 text-red-600 rounded-lg flex items-center justify-center transition duration-200 hover:bg-red-50 flex-shrink-0"
//                               >
//                                 <TrashIcon className="w-5 h-5" />
//                               </button>
//                             )}
//                           </div>
//                         );
//                       })}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Remove Depense Button */}

//           {depenses.length > 1 && isReadOnly === false && (
//             <button
//               type="button"
//               className="mt-5 bg-red-500 text-white px-5 py-2 rounded-full text-sm font-medium
//                  hover:bg-red-600 
//                  active:scale-95 
//                  transition-all duration-150"              
//               onClick={() => removeDepense(depense.id)}
//             >
//               Supprimer la date
//             </button>
//           )}
//         </div>
//       ))}

//       {/* Add Depense Button */}
//       {isReadOnly === false && (
//         <button
//           type="button"
//           className="mt-5 bg-blue-500 text-white px-5 py-2 rounded-full text-md font-medium
//                  hover:bg-blue-600 
//                  active:scale-95 
//                  transition-all duration-150"
//           onClick={addDepense}
//         >
//           + Ajouter une date
//         </button>
//       )}
//       {renderError("depenses")}

//       {/* Popup pour la prévisualisation */}
//                 {showPopup && selectedFile && (
//                   <FilePreviewModal
//                     selectedFile={selectedFile}
//                     fileUrl={fileUrl}
//                     onClose={() => setShowPopup(false)}
//                   />
//                 )}
//     </div>
//   );
// };

// export default DepenseSchema;

